import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Plus, ClipboardList, Calendar, Target, TrendingUp, 
  AlertCircle, CheckCircle2, Clock, Archive, Edit2, Trash2,
  ChevronRight, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PIP_CATEGORIES = [
  'Hospitalization reduction',
  'Missed visits',
  'Pain management',
  'Documentation compliance',
  'HOPE timeliness',
  'Revocation reduction',
  'After-hours response improvement',
  'Staff workload balance',
  'Patient satisfaction',
  'Safety improvement',
  'Other'
];

const TRIGGER_TYPES = [
  { value: 'data-driven', label: 'Data-Driven' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'incident', label: 'Incident' },
  { value: 'survey', label: 'Survey Finding' }
];

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active', icon: AlertCircle, color: 'bg-blue-500' },
  { value: 'Improving', label: 'Improving', icon: TrendingUp, color: 'bg-green-500' },
  { value: 'Sustained', label: 'Sustained', icon: CheckCircle2, color: 'bg-teal-500' },
  { value: 'Closed', label: 'Closed', icon: Archive, color: 'bg-gray-500' }
];

const PIPCard = ({ pip, onEdit, onDelete, onAddNote }) => {
  const status = STATUS_OPTIONS.find(s => s.value === pip.status) || STATUS_OPTIONS[0];
  const StatusIcon = status.icon;

  return (
    <Card className="stat-card group" data-testid={`pip-card-${pip.id}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`h-2 w-2 rounded-full ${status.color}`} />
            <Badge variant="outline" className="text-xs">{pip.category}</Badge>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => onEdit(pip)}
              data-testid={`edit-pip-${pip.id}`}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-destructive"
              onClick={() => onDelete(pip.id)}
              data-testid={`delete-pip-${pip.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <h3 className="font-semibold text-lg mb-2 line-clamp-2">{pip.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{pip.problem_statement}</p>
        
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Started:</span>
            <span>{pip.start_date ? new Date(pip.start_date).toLocaleDateString() : 'Not set'}</span>
          </div>
          
          {pip.target_benchmark && (
            <div className="flex items-center gap-2 text-sm">
              <Target className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Target:</span>
              <span className="truncate">{pip.target_benchmark}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-sm">
            <StatusIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Status:</span>
            <Badge className={`${status.color} text-white`}>{pip.status}</Badge>
          </div>
        </div>
        
        {pip.progress_notes?.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {pip.progress_notes.length} progress note{pip.progress_notes.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
        
        <Button 
          variant="ghost" 
          className="w-full mt-4 justify-between"
          onClick={() => onAddNote(pip)}
          data-testid={`add-note-${pip.id}`}
        >
          Add Progress Note
          <ChevronRight className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

const CreatePIPDialog = ({ open, onOpenChange, onSubmit, editingPIP }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    trigger_type: '',
    problem_statement: '',
    root_cause_summary: '',
    interventions: '',
    metrics_monitored: '',
    target_benchmark: '',
    start_date: new Date().toISOString().split('T')[0],
    expected_completion: '',
    status: 'Active'
  });

  useEffect(() => {
    if (editingPIP) {
      setFormData({
        title: editingPIP.title || '',
        category: editingPIP.category || '',
        trigger_type: editingPIP.trigger_type || '',
        problem_statement: editingPIP.problem_statement || '',
        root_cause_summary: editingPIP.root_cause_summary || '',
        interventions: editingPIP.interventions?.join(', ') || '',
        metrics_monitored: editingPIP.metrics_monitored?.join(', ') || '',
        target_benchmark: editingPIP.target_benchmark || '',
        start_date: editingPIP.start_date || new Date().toISOString().split('T')[0],
        expected_completion: editingPIP.expected_completion || '',
        status: editingPIP.status || 'Active'
      });
    } else {
      setFormData({
        title: '',
        category: '',
        trigger_type: '',
        problem_statement: '',
        root_cause_summary: '',
        interventions: '',
        metrics_monitored: '',
        target_benchmark: '',
        start_date: new Date().toISOString().split('T')[0],
        expected_completion: '',
        status: 'Active'
      });
    }
  }, [editingPIP, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const processed = {
      ...formData,
      interventions: formData.interventions.split(',').map(s => s.trim()).filter(Boolean),
      metrics_monitored: formData.metrics_monitored.split(',').map(s => s.trim()).filter(Boolean)
    };
    onSubmit(processed, editingPIP?.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{editingPIP ? 'Edit PIP' : 'Create New PIP'}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 pr-4">
          <form onSubmit={handleSubmit} className="space-y-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="title">PIP Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Reduce Hospitalization Rates"
                  required
                  data-testid="pip-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger data-testid="pip-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {PIP_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="trigger">Trigger Type *</Label>
                <Select 
                  value={formData.trigger_type} 
                  onValueChange={(v) => setFormData({ ...formData, trigger_type: v })}
                >
                  <SelectTrigger data-testid="pip-trigger-select">
                    <SelectValue placeholder="Select trigger" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGER_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="problem">Problem Statement *</Label>
                <Textarea
                  id="problem"
                  value={formData.problem_statement}
                  onChange={(e) => setFormData({ ...formData, problem_statement: e.target.value })}
                  placeholder="Describe the quality issue being addressed..."
                  className="min-h-[100px]"
                  required
                  data-testid="pip-problem-input"
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="rootcause">Root Cause Summary</Label>
                <Textarea
                  id="rootcause"
                  value={formData.root_cause_summary}
                  onChange={(e) => setFormData({ ...formData, root_cause_summary: e.target.value })}
                  placeholder="Summary of root cause analysis..."
                  className="min-h-[80px]"
                  data-testid="pip-rootcause-input"
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="interventions">Interventions (comma-separated)</Label>
                <Textarea
                  id="interventions"
                  value={formData.interventions}
                  onChange={(e) => setFormData({ ...formData, interventions: e.target.value })}
                  placeholder="e.g., Staff training, Process change, New protocol"
                  data-testid="pip-interventions-input"
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="metrics">Metrics Being Monitored (comma-separated)</Label>
                <Input
                  id="metrics"
                  value={formData.metrics_monitored}
                  onChange={(e) => setFormData({ ...formData, metrics_monitored: e.target.value })}
                  placeholder="e.g., Hospitalization rate, Missed visits, Pain scores"
                  data-testid="pip-metrics-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="target">Target Benchmark</Label>
                <Input
                  id="target"
                  value={formData.target_benchmark}
                  onChange={(e) => setFormData({ ...formData, target_benchmark: e.target.value })}
                  placeholder="e.g., Reduce to <5%"
                  data-testid="pip-target-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger data-testid="pip-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="start">Start Date *</Label>
                <Input
                  id="start"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  data-testid="pip-start-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="expected">Expected Completion</Label>
                <Input
                  id="expected"
                  type="date"
                  value={formData.expected_completion}
                  onChange={(e) => setFormData({ ...formData, expected_completion: e.target.value })}
                  data-testid="pip-expected-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="pip-submit-btn">
                {editingPIP ? 'Update PIP' : 'Create PIP'}
              </Button>
            </DialogFooter>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const AddNoteDialog = ({ open, onOpenChange, pip, onSubmit }) => {
  const [note, setNote] = useState('');
  const [status, setStatus] = useState(pip?.status || 'Active');

  useEffect(() => {
    if (pip) {
      setStatus(pip.status);
    }
    setNote('');
  }, [pip, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(pip.id, note, status);
    setNote('');
  };

  if (!pip) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Progress Note</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">{pip.title}</p>
            <Badge variant="outline">{pip.category}</Badge>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="note">Progress Note *</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Describe progress, findings, or updates..."
              className="min-h-[120px]"
              required
              data-testid="progress-note-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="updateStatus">Update Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="update-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {pip.progress_notes?.length > 0 && (
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Previous Notes</p>
              <ScrollArea className="h-32">
                <div className="space-y-2">
                  {pip.progress_notes.map((n, i) => (
                    <div key={i} className="text-xs p-2 bg-muted rounded">
                      <p className="font-medium">{n.by} - {n.date ? new Date(n.date).toLocaleDateString() : 'Unknown date'}</p>
                      <p className="text-muted-foreground mt-1">{n.note}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" data-testid="submit-note-btn">Add Note</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function PIPs() {
  const { token } = useAuth();
  const [pips, setPips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingPIP, setEditingPIP] = useState(null);
  const [selectedPIP, setSelectedPIP] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchPIPs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPips(data);
      }
    } catch (error) {
      toast.error('Failed to load PIPs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPIPs();
  }, [token]);

  const handleCreatePIP = async (data, editId) => {
    try {
      const url = editId ? `${API_URL}/api/pips/${editId}` : `${API_URL}/api/pips`;
      const method = editId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast.success(editId ? 'PIP updated' : 'PIP created');
        fetchPIPs();
        setCreateDialogOpen(false);
        setEditingPIP(null);
      } else {
        const error = await response.json();
        toast.error('Failed to save PIP', { description: error.detail });
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleDeletePIP = async (pipId) => {
    if (!window.confirm('Are you sure you want to delete this PIP?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/pips/${pipId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('PIP deleted');
        fetchPIPs();
      }
    } catch (error) {
      toast.error('Failed to delete PIP');
    }
  };

  const handleAddNote = async (pipId, note, status) => {
    try {
      const response = await fetch(`${API_URL}/api/pips/${pipId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ progress_note: note, status })
      });

      if (response.ok) {
        toast.success('Progress note added');
        fetchPIPs();
        setNoteDialogOpen(false);
        setSelectedPIP(null);
      }
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const filteredPIPs = filterStatus === 'all' 
    ? pips 
    : pips.filter(p => p.status === filterStatus);

  const statusCounts = {
    all: pips.length,
    Active: pips.filter(p => p.status === 'Active').length,
    Improving: pips.filter(p => p.status === 'Improving').length,
    Sustained: pips.filter(p => p.status === 'Sustained').length,
    Closed: pips.filter(p => p.status === 'Closed').length
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="pips-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pips-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Performance Improvement Projects</h1>
          <p className="text-muted-foreground mt-1">Track and manage quality improvement initiatives</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-pip-btn">
          <Plus className="h-4 w-4 mr-2" />
          New PIP
        </Button>
      </div>

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        {[
          { value: 'all', label: 'All' },
          ...STATUS_OPTIONS
        ].map(s => (
          <Button
            key={s.value}
            variant={filterStatus === s.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus(s.value)}
            data-testid={`filter-${s.value}`}
          >
            {s.label}
            <Badge variant="secondary" className="ml-2">
              {statusCounts[s.value]}
            </Badge>
          </Button>
        ))}
      </div>

      {/* PIPs Grid */}
      {filteredPIPs.length === 0 ? (
        <Card className="py-12">
          <CardContent className="flex flex-col items-center justify-center text-center">
            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No PIPs Found</h3>
            <p className="text-muted-foreground mb-4">
              {filterStatus === 'all' 
                ? 'Create your first Performance Improvement Project to get started.'
                : `No PIPs with status "${filterStatus}".`}
            </p>
            {filterStatus === 'all' && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First PIP
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPIPs.map((pip, index) => (
            <div 
              key={pip.id} 
              className="animate-fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <PIPCard
                pip={pip}
                onEdit={(p) => {
                  setEditingPIP(p);
                  setCreateDialogOpen(true);
                }}
                onDelete={handleDeletePIP}
                onAddNote={(p) => {
                  setSelectedPIP(p);
                  setNoteDialogOpen(true);
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <CreatePIPDialog
        open={createDialogOpen}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) setEditingPIP(null);
        }}
        onSubmit={handleCreatePIP}
        editingPIP={editingPIP}
      />

      <AddNoteDialog
        open={noteDialogOpen}
        onOpenChange={setNoteDialogOpen}
        pip={selectedPIP}
        onSubmit={handleAddNote}
      />
    </div>
  );
}
